import { FeatureTourDefinition } from '../types/featureTour';
import store from '@/store';
import { RUNNIT_TYPE } from '@run-diffusion/shared';

const TOUR_MEDIA_BASE = 'https://cdn.rundiffusion.com/file/rd-web-assets/image-ai/feature-tours/board-tour';
const boardTourStep1 = `${TOUR_MEDIA_BASE}/board_tour_step_1.mp4`;
const boardTourStep2 = `${TOUR_MEDIA_BASE}/board_tour_step_2.mp4`;
const boardTourStep3 = `${TOUR_MEDIA_BASE}/board_tour_step_3.mp4`;
const boardTourStep4 = `${TOUR_MEDIA_BASE}/board_tour_step_4.mp4`;
const boardTourStep5 = `${TOUR_MEDIA_BASE}/board_tour_step_5.mp4`;
const boardTourStep6 = `${TOUR_MEDIA_BASE}/board_tour_step_6.mp4`;
const boardTourStep7 = `${TOUR_MEDIA_BASE}/board_tour_step_7.mp4`;

// Steps 2–6 (tool-card-select through run-button) all describe interacting
// with a tool that's already on the board. On a brand-new empty board there
// are no nodes, so those steps would point at UI that doesn't exist — gate
// them behind this check so the tour collapses to just the welcome and
// "add another tool" steps in that case.
const boardHasTools = (): boolean => {
	const nodes = store.state.runnitNodes;
	return Array.isArray(nodes) && nodes.length > 0;
};

// Templates are read-only previews that surface a "Create Board" button in the
// drawer instead of the standard Run button, and don't expose an "Add another
// tool" affordance. Step 6 swaps to point at Create Board on templates, and
// the regular Run + add-tool steps gate themselves out.
const isTemplateView = (): boolean => {
	return store.state.runnitState?.runnit?.type === RUNNIT_TYPE.TEMPLATE;
};

// Every step from "settings-drawer-open" through "run-button" relies on the
// settings drawer being open for its target to be visible. Without re-asserting
// that state in each step's beforeEnter, Back-navigating from a later step
// (e.g. add-another-tool, which closes the drawer) would auto-skip backward
// past every drawer-internal step until it landed on a visible board element.
const ensureSettingsDrawerOpen = () => {
	const nodes = store.state.runnitNodes;
	const alreadyOpen = !!store.state.runnitState?.settingsDrawerOpen;
	if (nodes && nodes.length > 0 && !store.state.runnitState.selectedNodeId) {
		store.dispatch('updateRunnitState', {
			selectedNodeId: nodes[0].id,
			settingsDrawerOpen: true,
		});
	} else if (!alreadyOpen) {
		store.dispatch('updateRunnitState', {
			settingsDrawerOpen: true,
		});
	}
};

export const boardFirstOpen: FeatureTourDefinition = {
	id: 'board-first-open',
	title: 'A quick tour of Boards',
	version: 1,
	eyebrowLabel: 'Boards 101',
	// Auto-launch on the user's first visit to a board (workflow or template).
	// The launcher icon ("?") in the toolbar is the manual replay path; the
	// hasSeenTour() gate in maybeAutoStart prevents this from firing again
	// after the first time the tour is started/dismissed/completed.
	autoLaunch: true,
	steps: [
		{
			id: 'board-overview',
			target: '[data-feature-tour="board-canvas"]',
			title: 'Welcome to your board',
			description: 'A board is your creative workspace — a place to keep your favorite tools together, run them on their own, or chain a few into a workflow.',
			mediaUrl: boardTourStep1,
			mediaType: 'video',
			mediaPlaceholderLabel: 'Board overview',
			placement: 'right',
		},
		{
			id: 'tool-card-select',
			target: '[data-feature-tour="board-primary-tool-card"]',
			title: 'Every card is a tool',
			description: 'Each card on your board is a tool you can configure and run. Use them individually, or together as part of a multi-step workflow.',
			mediaUrl: boardTourStep2,
			mediaType: 'video',
			mediaPlaceholderLabel: 'Tool card interaction',
			placement: 'right',
			canShow: boardHasTools,
		},
		{
			id: 'settings-drawer-open',
			target: '.base-drawer.v-navigation-drawer--right.v-navigation-drawer--open',
			title: 'Shape the result here',
			description: 'Opening a card reveals its settings panel. Prompts, inputs, and generation controls all live here.',
			mediaUrl: boardTourStep3,
			mediaType: 'video',
			mediaPlaceholderLabel: 'Settings drawer',
			placement: 'left',
			canShow: boardHasTools,
			beforeEnter: ensureSettingsDrawerOpen,
		},
		{
			id: 'prompt-entry',
			target: '[data-feature-tour="board-prompt-field"]',
			title: 'Begin with a prompt',
			description: 'Describe what you want the tool to create or change. A clear prompt gives the model direction, style, and intent.',
			mediaUrl: boardTourStep4,
			mediaType: 'video',
			mediaPlaceholderLabel: 'Prompt input',
			placement: 'left',
			canShow: boardHasTools,
			beforeEnter: ensureSettingsDrawerOpen,
		},
		{
			id: 'core-settings',
			target: '[data-feature-tour="board-core-settings-field"]',
			title: 'Tune the output',
			description: 'Adjust aspect ratio and other tool controls. Small changes here can meaningfully shift what comes back.',
			mediaUrl: boardTourStep5,
			mediaType: 'video',
			mediaPlaceholderLabel: 'Core settings',
			placement: 'left',
			canShow: boardHasTools,
			beforeEnter: ensureSettingsDrawerOpen,
		},
		{
			id: 'run-button',
			target: '[data-feature-tour="board-run-button"]',
			title: 'Run the tool',
			description: 'When your setup looks right, hit Run. The token cost is shown here before generation begins.',
			mediaUrl: boardTourStep6,
			mediaType: 'video',
			mediaPlaceholderLabel: 'Run',
			placement: 'left',
			canShow: () => boardHasTools() && !isTemplateView(),
			beforeEnter: ensureSettingsDrawerOpen,
		},
		// Template variant of the run-button step: templates don't run in
		// place — the user has to clone the template into their own board
		// first via the Create Board button at the bottom of the drawer.
		// Same slot as run-button (only one of the two ever shows), with copy
		// retargeted at the action a template-viewer actually needs to take.
		{
			id: 'create-board-from-template',
			target: '[data-feature-tour="board-create-from-template"]',
			title: 'Make this template yours',
			description: 'You’re viewing a template, so you can’t generate here directly. Hit Create Board to spin up your own copy with these tools and settings ready to go.',
			mediaUrl: boardTourStep6,
			mediaType: 'video',
			mediaPlaceholderLabel: 'Create Board from template',
			placement: 'left',
			canShow: () => boardHasTools() && isTemplateView(),
			beforeEnter: ensureSettingsDrawerOpen,
			dismissOnTargetClick: true,
		},
		// Empty-board variant: there's no "another" tool to add yet, so the
		// copy needs to nudge the user toward their very first tool. Same
		// target element as the next step — only one of these renders.
		// dismissOnTargetClick lets the user click straight through to the
		// tool picker without having to also dismiss the tour card.
		{
			id: 'add-first-tool',
			target: '[data-feature-tour="board-add-tool"]',
			title: 'Add your first tool',
			description: 'Add your first tool to the board to get started. You can run it on its own, or chain a few together to build a workflow.',
			mediaUrl: boardTourStep7,
			mediaType: 'video',
			mediaPlaceholderLabel: 'Add your first tool',
			placement: 'top',
			canShow: () => !boardHasTools(),
			dismissOnTargetClick: true,
		},
		// Same dismissOnTargetClick treatment as the empty-board variant: this
		// is the last step, so clicking the target should dismiss the tour and
		// take the user straight into the tool picker without having to also
		// click the X on the card.
		{
			id: 'add-another-tool',
			target: '[data-feature-tour="board-add-tool"]',
			title: 'Add another tool',
			description: 'Drop another tool onto your board — keep it as a standalone, or chain it with the others to extend the workflow.',
			mediaUrl: boardTourStep7,
			mediaType: 'video',
			mediaPlaceholderLabel: 'Add another tool',
			placement: 'top',
			// Templates are read-only and don't expose an add-tool affordance,
			// so the step has no target to anchor on there.
			canShow: () => boardHasTools() && !isTemplateView(),
			beforeEnter: ensureSettingsDrawerOpen,
			dismissOnTargetClick: true,
		},
	],
};
